import { useState } from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, action, testid }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4 animate-fade-up" data-testid={testid}>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-stone-900">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-stone-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`rounded-xl border border-stone-200 bg-white shadow-sm ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const base =
  "h-11 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 disabled:bg-stone-100";

export function TextInput({ className, ...props }) {
  return <input {...props} className={cn(base, className)} />;
}

export function NumberInput({ className, ...props }) {
  return <input type="number" step="any" {...props} className={cn(base, "text-right", className)} />;
}

export function TextArea(props) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 ${props.className || ""}`}
    />
  );
}

export function SuggestInput({ value, onChange, options = [], onSelect, placeholder, ...props }) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const q = String(value || "").toLowerCase().trim();
  const filtered = options.filter((o) => String(o.label || "").toLowerCase().includes(q)).slice(0, 10);

  const pick = (o) => {
    onSelect ? onSelect(o) : onChange(o.label);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        {...props}
        autoComplete="off"
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        onKeyDown={(e) => {
          if (!open || !filtered.length) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, filtered.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          if (e.key === "Enter" && filtered[hi]) { e.preventDefault(); pick(filtered[hi]); }
          if (e.key === "Escape") setOpen(false);
        }}
        className={`${base} ${props.className || ""}`}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-lg border border-stone-200 bg-white py-1 shadow-lg">
          {filtered.map((o, i) => (
            <li
              key={o.value}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              className={`cursor-pointer px-3 py-2 text-sm ${i === hi ? "bg-emerald-50 text-emerald-900" : "text-stone-700 hover:bg-stone-50"}`}
            >
              {o.label}
              {o.hint ? <span className="ml-2 text-xs text-stone-400">{o.hint}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SelectInput({ options, placeholder = "Select...", ...props }) {
  return (
    <select {...props} className={`${base} ${props.className || ""}`}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function StatusBadge({ children, tone = "info" }) {
  const tones = {
    success: "bg-green-100 text-green-800 border-green-200",
    warning: "bg-amber-100 text-amber-800 border-amber-200",
    danger: "bg-red-100 text-red-800 border-red-200",
    info: "bg-blue-100 text-blue-800 border-blue-200",
    neutral: "bg-stone-100 text-stone-700 border-stone-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

export function CrudActions({ onEdit, onDelete, onRestore, inactive, testid }) {
  return (
    <div className="flex items-center justify-end gap-1">
      {inactive && onRestore ? (
        <button type="button" onClick={onRestore} data-testid={testid ? `${testid}-restore` : undefined}
          className="rounded-full border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-50">
          Restore
        </button>
      ) : (
        <>
          {onEdit && (
            <button type="button" onClick={onEdit} data-testid={testid ? `${testid}-edit` : undefined}
              className="rounded-full border border-stone-200 px-2.5 py-1 text-[11px] font-semibold text-stone-700 hover:bg-stone-50">
              Edit
            </button>
          )}
          {onDelete && (
            <button type="button" onClick={onDelete} data-testid={testid ? `${testid}-delete` : undefined}
              className="rounded-full border border-red-200 px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50">
              Delete
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function SectionTitle({ children }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-700">
      <span className="h-px w-4 bg-emerald-300" />
      {children}
    </div>
  );
}
